import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import {
    percentChangeFormatter, getNumberWithComas, formatUnitOrPercentage, getCurrencyFormat
} from '../../../../helpers/dataFormatters';
import { agentProductionTerms } from '../../../../../constants';
import {
    dealStatusCodes,
    mlsAreaTypes,
    agentCoverageListingTabs,
    coverageListingRoute
} from '../../../../constants/agentProductionConstants';
import { dataPointMouseOverStyler } from '../../../../helpers/schemaTableHelpers';
import { tableChartModes } from '../../../common/TableChartToggle';
import { useAgentProfileStore } from '../../../../../store/store';
import { COLOR_PRIMARY_GREEN, COLOR_SECONDARY_GREEN } from '../../../../../common/constants/colors';

const AgentTransactionCoverageChart = props => {
    const {
        agentData,
        isUnits,
        statusType,
        areaType,
        shouldDisplayAreaPercentage,
        agent,
        tableType,
        timePeriod,
        isPDF,
    } = props;

    const {
        setAreaList,
        setSelectedMlsId,
        setSelectedArea,
        setAreaType,
        setTableType,
        setIsUnits,
        setAgentCoverageListingTab,
        setTimePeriod
    } = useAgentProfileStore(state => ({
        setAreaList: state.setAreaList,
        setSelectedAgentId: state.setSelectedAgentId,
        setSelectedMlsId: state.setSelectedMlsId,
        setSelectedArea: state.setSelectedArea,
        setAreaType: state.setAreaType,
        setTableType: state.setTableType,
        setIsUnits: state.setIsUnits,
        setAgentCoverageListingTab: state.setAgentCoverageListingTab,
        setTimePeriod: state.setTimePeriod
    }));

    const history = useHistory();
    const CONTAINER_WIDTH_MAX = '100%';
    const MINIMUM_COLUMN_WIDTH_IN_PIXELS = 85;
    const ONE_MILLION = 1000000;
    const PDF_WIDTH = 1879;

    const [widthGraph, setWidthGraph] = useState(CONTAINER_WIDTH_MAX);
    const refGraphContainer = useRef(null);

    const countColumns = agentData ? agentData.length : null;
    const widthGraphContent = countColumns * MINIMUM_COLUMN_WIDTH_IN_PIXELS;
    const widthContainer = refGraphContainer.current?.clientWidth ?? 0;

    useEffect(() => {
        if (widthGraphContent > widthContainer) {
            setWidthGraph(`${widthGraphContent}px`);
        } else {
            setWidthGraph(CONTAINER_WIDTH_MAX);
        }
    }, [widthGraphContent, widthContainer]);

    const xAxisLabels = agentData?.map((d) => d.area);

    const dataPointGroups = [];
    if (statusType === dealStatusCodes.SOLD.value) {
        const listSideSoldVolume = agentData?.map(
            (d) => d.listVolume
        );
        const sellSideSoldVolume = agentData?.map(
            (d) => d.sellVolume
        );
        const listSideSoldUnits = agentData?.map(
            (d) => d.listUnits
        );
        const sellSideSoldUnits = agentData?.map(
            (d) => d.sellUnits
        );

        dataPointGroups.push({
            name: 'List Side (Sold)',
            data: isUnits
                ? listSideSoldUnits
                : listSideSoldVolume
        });
        dataPointGroups.push({
            name: 'Sell Side (Sold)',
            data: isUnits
                ? sellSideSoldUnits
                : sellSideSoldVolume
        });
    } else {
        const chartName = isUnits ? 'Total #' : 'Total $';
        const dataPoints = isUnits
            ? agentData?.map(d => d.totalUnits)
            : agentData?.map((d) => d.totalVolume);

        dataPointGroups.push({
            name: chartName,
            data: dataPoints
        });
    }

    const getTooltipContent = (series, dataPointIndex) => {
        const data = agentData[dataPointIndex];
        const percentage = isUnits
            ? percentChangeFormatter(data.totalUnitsPct)
            : percentChangeFormatter(data.totalVolumePct);
        const percentageElement = statusType === dealStatusCodes.SOLD.value
            ? `<div class='total-percent'>
                <span>${percentage}</span>
            </div>`
            : `<span>${percentage}</span>`;
        const soldTooltipInfo = statusType === dealStatusCodes.SOLD.value
            ? `<div class='items'>
                <span class='bullet' style='background-color: ${COLOR_SECONDARY_GREEN}'></span>
                ${isUnits ? getNumberWithComas(data.sellUnits) : getCurrencyFormat(data.sellVolume)} <br />
                <span class='bullet' style='background-color: ${COLOR_PRIMARY_GREEN}'></span>
                ${isUnits ? getNumberWithComas(data.listUnits) : getCurrencyFormat(data.listVolume)}
            </div>`
            : '';

        return `<div class='arrow_box volume-chart-tooltip'>
            <div class='header'>
                ${data.area}
            </div>
            <div class='body'>
                ${percentageElement}
                ${soldTooltipInfo}
                <div class='total'>
                    <span>Total:</span>
                    <span>${isUnits ? getNumberWithComas(data.totalUnits) : getCurrencyFormat(data.totalVolume)}</span>
                </div>
            </div>
        </div>`;
    };

    const yAxisLabel = isUnits ? '# Units' : '$ in Millions';
    const colors = [];
    statusType === dealStatusCodes.SOLD.value && colors.push(COLOR_SECONDARY_GREEN, COLOR_PRIMARY_GREEN);
    statusType === dealStatusCodes.UNDER_CONTRACT.value && colors.push(COLOR_PRIMARY_GREEN);
    statusType === dealStatusCodes.ACTIVE.value && colors.push(COLOR_PRIMARY_GREEN);
    const radius = 1;
    const fontFamily =
        '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,' +
        'sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"';

    const getChartOptions = (isPDF) => ({
        chart: {
            type: 'bar',
            stacked: true,
            toolbar: {
                show: false
            },
            fontFamily,
            noData: {
                text: agentProductionTerms.noDataForSelectedAgent
            },
            zoom: {
                enabled: false
            },
            events: {
                mounted: (chart) => {
                    chart.windowResizeHandler();
                },
                dataPointSelection: (event, chartContext, config) => {
                    setAgentCoverageListingTab(agentCoverageListingTabs.TOTAL_SOLD);
                    setSelectedMlsId(agent.mlsSid);
                    setAreaList(xAxisLabels);
                    setSelectedArea(xAxisLabels[config.dataPointIndex]);
                    setAreaType(areaType);
                    setTableType(tableType);
                    setTimePeriod(timePeriod);
                    setIsUnits(isUnits);

                    history.push({
                        pathname: coverageListingRoute,
                        state: {
                            agent,
                            selectedArea: xAxisLabels[config.dataPointIndex],
                            displayMode: tableChartModes.CHART
                        }
                    });
                },
                dataPointMouseEnter: dataPointMouseOverStyler
            }
        },
        responsive: [
            {
                breakpoint: 480,
                options: {
                    legend: {
                        position: 'left',
                        offsetX: -10,
                        offsetY: 0
                    }
                }
            }
        ],
        plotOptions: {
            bar: {
                horizontal: false,
                borderRadius: radius,
                columnWidth: '40px',
                dataLabels: {
                    position: 'top',
                    hideOverflowingLabels: false
                }
            }
        },
        dataLabels: {
            enabled: shouldDisplayAreaPercentage,
            enabledOnSeries: statusType === dealStatusCodes.SOLD.value ? [1, 2] : [0],
            textAnchor: 'middle',
            formatter: function(value, { seriesIndex, dataPointIndex, w }) {
                const totalPercentage = isUnits
                    ? percentChangeFormatter(agentData[dataPointIndex].totalUnitsPct)
                    : percentChangeFormatter(agentData[dataPointIndex].totalVolumePct);
                return totalPercentage ?? 0;
            },
            style: {
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 'bold',
                colors: ['#212529']
            },
            offsetY: -30
        },
        xaxis: {
            type: 'category',
            categories: xAxisLabels,
            labels: {
                formatter: function(value) {
                    if (areaType === mlsAreaTypes.MLS_AREAS) return value.split(' ', 1)[0];
                    return value;
                },
                trim: true,
                hideOverlappingLabels: false
            }
        },
        yaxis: {
            labels: {
                formatter: function(value) {
                    if (isUnits) {
                        return formatUnitOrPercentage(value);
                    } else {
                        return formatUnitOrPercentage((parseInt(value) / ONE_MILLION), false, true);
                    }
                }
            },
            tickAmount: 16,
            title: {
                text: yAxisLabel,
                style: {
                    cssClass: 'agent-coverage-chart-y-label'
                }
            }
        },
        tooltip: {
            custom: ({ series, _, dataPointIndex }) =>
                getTooltipContent(series, dataPointIndex)
        },
        legend: {
            inverseOrder: false,
            markers: {
                fillColors: colors,
                width: 15,
                height: 15,
                radius: 10
            },
            fontFamily,
            itemMargin: {
                horizontal: 10
            },
            position: 'top',
            offsetX: 0,
            horizontalAlign: 'left',
            fontSize: isPDF ? '16px' : undefined,
        },
        fill: {
            opacity: 1,
            colors: colors.reverse()
        }
    });

    const chartProps = {
        options: getChartOptions(isPDF),
        series: dataPointGroups,
        type: 'bar',
        height: 400,
        width: isPDF ? PDF_WIDTH : widthGraph
    };

    const overflow = widthGraph === CONTAINER_WIDTH_MAX ? null : 'tableOverflow';
    const pdfClasses = isPDF ? "pdf-chart chart-capture-area" : "";

    let outputToShow;

    if (!agentData || agentData.length === 0) {
        outputToShow = (
            <div className={'mt-3'}>Your search returned no results</div>
        );
    } else if (widthContainer === 0) {
        outputToShow = null;
    } else {
        outputToShow = <Chart {...chartProps} />;
    }

    return (
        <div
            ref={refGraphContainer}
            className={`${overflow} ${pdfClasses} agent-contact-chart-wrapper py-4 px-3`}
            style={isPDF ? {} : { maxWidth: 'none' }}
            id='agent-coverage-chart-container'
        >
            {outputToShow}
        </div>
    );
};

AgentTransactionCoverageChart.propTypes = {
    agentData: PropTypes.array,
    isUnits: PropTypes.bool,
    statusType: PropTypes.string,
    areaType: PropTypes.string,
    shouldDisplayAreaPercentage: PropTypes.bool,
    agent: PropTypes.object,
    tableType: PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    }),
    timePeriod: PropTypes.string,
    isPDF: PropTypes.bool,
};

export default AgentTransactionCoverageChart;
